import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import SeoMeta, { breadcrumbJsonLd } from "@/components/SeoMeta";
import Breadcrumbs from "@/components/Breadcrumbs";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import { ArrowRight, Camera, MapPin, Warning } from "@phosphor-icons/react";

export default function CatalogueHub() {
  const { hub: hubSlug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null); setMissing(false);
    http.get(`/catalogue/${hubSlug}`)
      .then(({ data }) => setData(data.hub))
      .catch(() => setMissing(true));
  }, [hubSlug]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="catalogue-hub">
      {data && (
        <SeoMeta
          title={`${data.label} — Environmental Catalogue`}
          description={data.intro.slice(0, 155)}
          canonical={`${origin}/explore/${data.slug}`}
          jsonLd={breadcrumbJsonLd([
            { name: "Home", url: origin },
            { name: "Catalogue", url: `${origin}/explore` },
            { name: data.label, url: `${origin}/explore/${data.slug}` },
          ])}
        />
      )}
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        {missing ? (
          <h1 className="font-serif-display text-3xl" data-testid="catalogue-hub-missing">Unknown catalogue hub.</h1>
        ) : !data ? (
          <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase">Loading…</div>
        ) : (
          <>
            <Breadcrumbs items={[
              { label: "Home", to: "/" },
              { label: "Catalogue", to: "/explore" },
              { label: data.label },
            ]} />

            <h1 className="mt-4 font-serif-display text-4xl md:text-6xl leading-[1.0] tracking-tight" data-testid="hub-title">{data.label}.</h1>
            <p className="mt-4 max-w-3xl text-[#3A3830] text-sm md:text-base leading-relaxed" data-testid="hub-intro">{data.intro}</p>
            <div className="mt-3"><ListenTranslateBar text={`${data.label}. ${data.intro}`} testidPrefix="hub-tools" /></div>

            {data.safety_note && (
              <div className="mt-5 rounded-xl border border-[#7C2D12]/30 bg-[#7C2D12]/8 p-4 text-sm text-[#7C2D12] flex gap-2" data-testid="hub-safety">
                <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
                <span>{data.safety_note}</span>
              </div>
            )}

            {/* CTAs */}
            <div className="mt-6 flex flex-wrap gap-2.5">
              {data.ctas?.map((c) => (
                <Link key={c.label} to={c.to} data-testid={`hub-cta-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={c.kind === "primary"
                    ? "inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:opacity-90"
                    : "inline-flex items-center gap-1.5 rounded-full border border-[#1B4332]/30 px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-[#1B4332]/5"}>
                  <Camera size={13} weight="bold" /> {c.label}
                </Link>
              ))}
            </div>

            {/* Core sections */}
            <div className="mt-10" data-testid="hub-sections">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-3">Inside this hub</div>
              <div className="flex flex-wrap gap-1.5">
                {data.sections.map((s) => (
                  <span key={s} className="rounded-full border border-black/10 bg-white px-3 py-1.5 text-xs text-[#3A3830]">{s}</span>
                ))}
              </div>
            </div>

            {/* Entities */}
            <div className="mt-12" data-testid="hub-entities">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-serif-display text-2xl md:text-3xl">{data.label} pages</h2>
                <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]/60">{data.entity_count} curated</span>
              </div>
              {data.entities?.length === 0 ? (
                <p className="text-sm text-[#3A3830]">Curated pages for this hub are being prepared.</p>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {data.entities.map((e) => (
                    <Link key={e.slug} to={`/explore/${data.slug}/${e.slug}`} data-testid={`hub-entity-${e.slug}`}
                      className="group rounded-2xl bg-white border border-black/10 p-5 hover:border-[#1B4332] hover:-translate-y-0.5 transition-all">
                      <div className="font-serif-display text-xl leading-tight">{e.title}</div>
                      {e.scientific_name && <div className="mt-0.5 font-serif-display italic text-sm text-[#3A3830]/80">{e.scientific_name}</div>}
                      <p className="mt-2 text-sm text-[#3A3830] line-clamp-3">{e.summary}</p>
                      <div className="mt-2.5 inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                        Open page <ArrowRight size={10} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Recent community observations */}
            <div className="mt-12" data-testid="hub-recent">
              <h2 className="font-serif-display text-2xl md:text-3xl mb-3 flex items-center gap-2">
                <MapPin size={20} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> Recently observed
              </h2>
              {data.recent_observations?.length === 0 ? (
                <p className="text-sm text-[#3A3830]">No community observations in this hub yet — yours could be the first.</p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {data.recent_observations.map((o) => (
                    <Link key={o.id} to={`/observations/${o.id}`} data-testid={`hub-obs-${o.id}`}
                      className="rounded-xl border border-black/10 bg-white p-4 hover:border-[#1B4332] transition-colors">
                      <div className="font-serif-display text-base leading-tight">{o.title || "Observation"}</div>
                      <div className="mt-1 font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#3A3830]/60">
                        {o.category?.replace(/-/g, " ")} · {o.date}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
